import { Grid, TextField } from "@material-ui/core";
import { useParams } from "react-router-dom";
import { planEatApi } from "../../../../planEatApi/PlanEatApi";
import { ICompanyContent } from "../../../../planEatApi/PlanEatFacade";
import { AdminPicker } from "../../components/AdminPicker";
import { EntityDetailLayout, Section } from "../../components/EntityDetailLayout";
import { EntityEditorConfig, useEntityEditor } from "../../components/useEntityEditor";
import { hierarchyCrumbs } from "../../components/VersionsPage";
import { useSysadminData } from "../../SysadminData";
import { sysadminPaths } from "../../sysadminUtils";
import { OfficesTable } from "../Office/Offices";

const companyConfig: EntityEditorConfig<ICompanyContent> = {
    label: "Company",
    section: "companies",
    empty: {
        id: "",
        name: "",
        description: "",
        companyName: "",
        companyCvr: "",
        companyAddress: "",
        companyPhoneNumber: "",
        companyAdminIds: [],
    },
    get: planEatApi.getCompany,
    getVersion: planEatApi.getVersionForCompany,
    post: planEatApi.postCompany,
    remove: planEatApi.postDeleteForCompany,
};

export const Company: React.FC = () => {
    const { companyId, version } = useParams<{ companyId?: string; version?: string }>();
    const data = useSysadminData();
    const editor = useEntityEditor(companyConfig, companyId, version);
    const { entity: company, setField, isVersion } = editor;

    const missing: string[] = [];
    if (!company.companyName.trim()) missing.push("name");
    if ((company.companyAdminIds ?? []).length === 0) missing.push("at least one admin");

    const officeCount = data.offices.filter(o => o.content.companyId === company.id).length;
    const title = editor.isNew ? "New company" : company.companyName || "Unnamed company";

    return (
        <EntityDetailLayout
            label="Company"
            section="companies"
            title={title}
            breadcrumbs={[...hierarchyCrumbs(data, "companies", {}), { label: title }]}
            editor={editor}
            missing={missing}
            parentPath={sysadminPaths.list("companies")}
            deleteWarning={officeCount > 0 ? <p>The company still has <b>{officeCount} office(s)</b>. They are not deleted automatically.</p> : undefined}
            related={<OfficesTable companyId={company.id} title="Offices" />}
        >
            <Section title="Details">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth required variant="outlined" label="Name" value={company.companyName} disabled={isVersion}
                            onChange={e => setField("companyName", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth variant="outlined" label="CVR" value={company.companyCvr} disabled={isVersion}
                            onChange={e => setField("companyCvr", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth variant="outlined" label="Address" value={company.companyAddress} disabled={isVersion}
                            onChange={e => setField("companyAddress", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth variant="outlined" label="Phone number" value={company.companyPhoneNumber} disabled={isVersion}
                            onChange={e => setField("companyPhoneNumber", e.target.value)} />
                    </Grid>
                </Grid>
            </Section>
            <Section title="Admins" description="Admins are given the company_manager_users role.">
                <AdminPicker value={company.companyAdminIds ?? []} readOnly={isVersion} onChange={ids => setField("companyAdminIds", ids)} />
            </Section>
        </EntityDetailLayout>
    );
};
